import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { InvitationService } from '@/core/application/profile/InvitationService';

interface ActiveInvite {
  id: string;
  code: string;
  expires_at: string;
}

export function GenerateInviteScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeInvites, setActiveInvites] = useState<ActiveInvite[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    loadActiveInvites();
  }, []);

  const loadActiveInvites = async () => {
    try {
      const invites = await InvitationService.getActiveInvitations();
      setActiveInvites(invites);
    } catch (e) {
      console.error("Error loading active invitations", e);
    } finally {
      setLoadingList(false);
    }
  };

  const getBaseUrl = () => {
    return globalThis.location.origin;
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleGenerateNew = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const newCode = await InvitationService.generateInvitationCode();
      await loadActiveInvites();
      handleCopyOrShareSpecific(newCode);
    } catch (err: any) {
      setError(err.message || 'No se pudo generar la invitación.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyOrShareSpecific = async (code: string) => {
    setError(null);
    setSuccessMessage(null);
    try {
      const inviteUrl = `${getBaseUrl()}/invite?code=${code}`;
      const message = `¡Hola! Únete a Aura Clínico como Profesional Verificado.\n\nToca este enlace mágico para acceder:\n${inviteUrl}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Invitación a Aura Clínico',
            text: message,
            url: inviteUrl
          });
          showSuccess('¡Compartido con éxito!');
        } catch {
          await navigator.clipboard.writeText(message);
          showSuccess('¡Enlace copiado al portapapeles!');
        }
      } else {
        await navigator.clipboard.writeText(message);
        showSuccess('¡Enlace copiado al portapapeles!');
      }
    } catch (err: any) {
      console.error("Error compartiendo:", err);
      setError('No se pudo copiar o compartir el enlace.');
    }
  };

  const handleGoBack = () => {
    if (globalThis.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/consumer');
    }
  };

  const remaining = Math.max(0, 3 - activeInvites.length);

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.headerWrapper}>
          <div style={styles.header}>
            <button onClick={handleGoBack} style={styles.backButton}>
              <ArrowLeft size={20} color="#3B82F6" style={{ marginRight: '6px' }} />
              <span style={styles.backText}>Volver</span>
            </button>
            <h1 style={styles.title}>Invitar Colega</h1>
          </div>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>
            Invita hasta 3 colegas de confianza a unirse a la red de Aura Clínico.
          </p>
          
          <div style={styles.remainingBadge}>
            <span style={styles.remainingText}>
              Invitaciones disponibles: {loadingList ? '...' : `${remaining}/3`}
            </span>
          </div>
          
          <p style={styles.warning}>
            Al invitar a otros médicos, ofreces a tus pacientes un ecosistema integral y seguro. Una vez que un invitado se registre, la invitación desaparecerá de esta lista y recuperarás ese cupo.
          </p>

          {Boolean(error) && (
            <div style={styles.errorContainer}>
              <span style={styles.errorText}>{error}</span>
            </div>
          )}

          {Boolean(successMessage) && (
            <div style={styles.successContainer}>
              <span style={styles.successText}>{successMessage}</span>
            </div>
          )}

          {/* Botón para generar nueva si hay cupo */}
          <button 
            style={{ ...styles.button, ...(remaining === 0 && !loading ? styles.buttonDisabled : {}) }} 
            onClick={handleGenerateNew}
            disabled={loading || remaining === 0}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={styles.spinner} />
                <span style={styles.buttonText}>Asegurando nueva invitación...</span>
              </div>
            ) : (
              <span style={styles.buttonText}>Generar Nueva Invitación</span>
            )}
          </button>

          {/* Lista de Invitaciones Activas */}
          <div style={styles.listContainer}>
            <h2 style={styles.listTitle}>Tus Enlaces Pendientes</h2>
            {loadingList && <div style={{ ...styles.spinnerDark, marginTop: '20px' }} />}
            {!loadingList && activeInvites.length === 0 && (
              <p style={styles.emptyText}>No tienes invitaciones pendientes por usar.</p>
            )}
            {!loadingList && activeInvites.length > 0 && activeInvites.map((inv, index) => (
              <div key={inv.id} style={styles.inviteCard}>
                <div style={styles.inviteInfo}>
                  <div style={styles.inviteCode}>Invitación #{index + 1}</div>
                  <div style={styles.inviteExpiry}>Expira: {new Date(inv.expires_at).toLocaleDateString()}</div>
                </div>
                <button 
                  style={styles.copyButton}
                  onClick={() => handleCopyOrShareSpecific(inv.code)}
                >
                  <Share2 size={16} color="#3B82F6" style={{ marginRight: '6px' }} />
                  <span style={styles.copyButtonText}>Compartir</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  safeArea: { flex: 1, backgroundColor: '#F8FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  container: { flex: 1, display: 'flex', flexDirection: 'column', width: '100%' },
  headerWrapper: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #F1F5F9', width: '100%' },
  header: { display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '20px', width: '100%', maxWidth: '700px', margin: '0 auto' },
  backButton: { marginRight: '16px', display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  backText: { fontSize: '16px', color: '#3B82F6', fontWeight: 500 },
  title: { fontSize: '20px', fontWeight: 700, color: '#0F172A', margin: 0 },
  content: { padding: '24px', width: '100%', maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column' },
  description: { fontSize: '16px', color: '#334155', lineHeight: '24px', marginBottom: '12px', margin: '0 0 12px 0' },
  remainingBadge: { backgroundColor: '#F1F5F9', alignSelf: 'flex-start', padding: '6px 12px', borderRadius: '16px', marginBottom: '20px' },
  remainingText: { color: '#0F172A', fontWeight: 600, fontSize: '14px' },
  warning: { fontSize: '14px', color: '#1E3A8A', backgroundColor: '#DBEAFE', padding: '16px', borderRadius: '8px', lineHeight: '20px', marginBottom: '24px', overflow: 'hidden', margin: '0 0 24px 0' },
  errorContainer: { backgroundColor: '#FEE2E2', padding: '12px', borderRadius: '8px', marginBottom: '20px' },
  errorText: { color: '#EF4444', fontSize: '14px', textAlign: 'center', display: 'block' },
  successContainer: { backgroundColor: '#DCFCE7', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #86EFAC' },
  successText: { color: '#166534', fontSize: '15px', textAlign: 'center', fontWeight: 600, display: 'block' },
  button: { backgroundColor: '#0F172A', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '32px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s', width: '100%' },
  buttonDisabled: { opacity: 0.7, cursor: 'not-allowed' },
  buttonText: { color: '#FFFFFF', fontSize: '16px', fontWeight: 600 },
  listContainer: { borderTop: '1px solid #E2E8F0', paddingTop: '24px', display: 'flex', flexDirection: 'column' },
  listTitle: { fontSize: '18px', fontWeight: 700, color: '#0F172A', marginBottom: '16px', margin: '0 0 16px 0' },
  emptyText: { fontSize: '14px', color: '#64748B', fontStyle: 'italic', margin: 0 },
  inviteCard: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FFFFFF', padding: '16px', borderRadius: '12px', marginBottom: '12px', border: '1px solid #E2E8F0' },
  inviteInfo: { flex: 1, display: 'flex', flexDirection: 'column' },
  inviteCode: { fontSize: '16px', fontWeight: 600, color: '#0F172A', marginBottom: '4px' },
  inviteExpiry: { fontSize: '12px', color: '#64748B' },
  copyButton: { backgroundColor: '#F1F5F9', padding: '8px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', border: 'none', cursor: 'pointer' },
  copyButtonText: { color: '#3B82F6', fontWeight: 600, fontSize: '14px' },
  spinner: { width: '20px', height: '20px', border: '2px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '10px' },
  spinnerDark: { width: '24px', height: '24px', border: '3px solid #0F172A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
